class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.cart-block-close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /**
   * Сессия "открытия по добавлению" = 2 часа.
   * Drawer открывается только при первом добавлении в корзину за сессию;
   * при последующих добавлениях только обновляется контент без открытия.
   */
  static get CART_DRAWER_ADD_SESSION_KEY() {
    return 'frostix_cart_drawer_add_opened_at';
  }
  static get CART_DRAWER_ADD_SESSION_MS() {
    return 2 * 60 * 60 * 1000; // 2 часа
  }

  shouldOpenDrawerAfterAdd() {
    try {
      const raw = sessionStorage.getItem(CartDrawer.CART_DRAWER_ADD_SESSION_KEY);
      if (!raw) return true;
      const openedAt = parseInt(raw, 10);
      if (isNaN(openedAt)) return true;
      return (Date.now() - openedAt) >= CartDrawer.CART_DRAWER_ADD_SESSION_MS;
    } catch (e) {
      return true;
    }
  }

  markDrawerOpenedAfterAdd() {
    try {
      sessionStorage.setItem(CartDrawer.CART_DRAWER_ADD_SESSION_KEY, Date.now().toString());
    } catch (e) {}
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    // Render sections if available
    if (parsedState.sections && Object.keys(parsedState.sections).length > 0) {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        
        const sectionHtml = parsedState.sections[section.id] || 
                           (section.section && parsedState.sections[section.section]) ||
                           parsedState.sections['cart-drawer'] ||
                           parsedState.sections['CartDrawer'] ||
                           parsedState.sections['cart-icon-bubble'];
        
        if (sectionHtml) {
          sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
        }
      });
    } else {
      // Fallback: if no sections, fetch them
      if (parsedState.item_count !== undefined || parsedState.total_price !== undefined) {
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        if (cartDrawerItems) {
          cartDrawerItems.onCartUpdate();
        }
      }
    }

    // Открывать drawer только один раз за сессию (2 ч) при добавлении товара
    const openDrawerThisTime = this.shouldOpenDrawerAfterAdd();
    if (openDrawerThisTime) {
      this.markDrawerOpenedAfterAdd();
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (openDrawerThisTime) {
        this.open();
      }
    });

    // Реактивная проверка упаковки (debounced через PackagingUtils)
    if (window.PackagingUtils) {
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      const sections = cartDrawerItems
        ? cartDrawerItems.getSectionsToRender().map((s) => s.section)
        : [];
      window.PackagingUtils.ensurePackaging(parsedState, sections, (result) => {
        if (result && result.sections) {
          // Перерендерить drawer с обновлёнными секциями
          this.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            if (!sectionElement) return;
            const html = result.sections[section.section] || result.sections[section.id];
            if (html) {
              sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
            }
          });
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const effectiveSelector = selector || '.shopify-section';
    return new DOMParser().parseFromString(html, 'text/html').querySelector(effectiveSelector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: null,
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
